// uiarc: metric-card (https://uiarc.dev/r/metric-card.json) — CSS Modules yerine Tailwind. Üstte etiket ve işaretli
// değişim hapı, ortada çark sayaç (`animated-counter`), altta bağlam satırı. Etiket/bağlam değişince yeni metin
// büyük sayıya doğru yükselir ya da alçalır; değişim hapı genişliğini yayla izler, yönü renkle de okunur.
// Sapmalar: API proje sözleşmesi (`value` biçimli metin, `change` yüzde sayısı, `hint`); sayı Türkçe biçimiyle gelir
// (`lib/format.ts`), sayaç metni basamaklara böler. Metin dışı `value` (iskelet, hata) olduğu gibi çizilir.
// Yön işareti (`+`/`-`) metinde zaten durur; uiarc gibi ayrı simge yok.
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  type Variants,
} from 'motion/react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { AnimatedCounter } from '@/components/stat/animated-counter'
import { numericValue } from '@/lib/counter-parts'
import { formatChange } from '@/lib/format'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const { blur, duration, ease, spring } = motionTokens

/** Sayı taşıyan metin hareket ettiği yönden girer: büyük değer aşağıdan yükselir, küçük olan yukarıdan iner. */
const rise: Variants = {
  hidden: (direction: number) => ({
    opacity: 0,
    y: `${0.3 * direction}em`,
    filter: `blur(${blur.soft}px)`,
  }),
  shown: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: duration.standard, ease: ease.enter },
  },
  gone: (direction: number) => ({
    opacity: 0,
    y: `${-0.3 * direction}em`,
    filter: `blur(${blur.subtle}px)`,
    transition: { duration: duration.fast, ease: ease.standard },
  }),
}
const fade: Variants = {
  hidden: { opacity: 0, y: 0, filter: 'blur(0px)' },
  shown: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: duration.instant } },
  gone: { opacity: 0, y: 0, filter: 'blur(0px)', transition: { duration: duration.instant } },
}

/** Yeni metin yükselirken eskisi çıkar; `morph` kaplayıcıyı yeni metnin genişliğine yayla uydurur, sıçratmaz. */
function Swap({
  text,
  morph = false,
  block = false,
}: {
  text: string
  morph?: boolean
  block?: boolean
}) {
  const reduceMotion = !!useReducedMotion()
  const sizer = useRef<HTMLSpanElement>(null)
  const width = useMotionValue<number | 'auto'>('auto')
  const [shown, setShown] = useState({ text, direction: 1 })
  if (shown.text !== text)
    setShown({ text, direction: numericValue(text) < numericValue(shown.text) ? -1 : 1 })
  useEffect(() => {
    const node = sizer.current
    if (!node || typeof ResizeObserver === 'undefined') return
    let measured: string | null = null
    // Yerleşim boyutu (dönüşümlü kutu değil): ölçekleyen üst öğe metni kırpmaz. Yalnız yeni metin yaylanır; font yüklemesi atlar.
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const next = entry.borderBoxSize?.[0]?.inlineSize ?? node.offsetWidth
      if (next && measured !== null && measured !== node.dataset.text && !reduceMotion)
        animate(width, next, spring.morph)
      else width.jump(next || 'auto')
      measured = next ? (node.dataset.text ?? null) : null
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [morph, reduceMotion, width])
  return (
    <motion.span
      className={cn(
        'relative max-w-full',
        block ? 'block' : 'inline-flex overflow-x-clip align-top whitespace-nowrap',
      )}
      style={morph ? { width } : undefined}
    >
      {morph && (
        <span
          ref={sizer}
          // Metin CSS ile çizilir: ölçü kutusu DOM'da ikinci bir kopya (ve sorgu eşleşmesi) olmasın.
          data-text={text}
          className="pointer-events-none invisible absolute top-0 left-0 whitespace-nowrap after:content-[attr(data-text)]"
          aria-hidden="true"
        />
      )}
      <AnimatePresence mode="popLayout" initial={false} custom={shown.direction}>
        <motion.span
          key={text}
          className={block ? 'block' : 'inline-block'}
          custom={shown.direction}
          variants={reduceMotion ? fade : rise}
          initial="hidden"
          animate="shown"
          exit="gone"
        >
          {text}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  )
}

/**
 * Özet (KPI) kartı. `change` önceki döneme göre yüzde değişimdir; yön işaretle ve renkle gösterilir
 * (artış = success, azalış = danger). `animate={false}`: sayaç ve metin geçişi yok (yazdırma çıktısı).
 */
/**
 * Değer boyutu kartın iç genişliğinden: mobilde yan yana iki kartın ~133 px'ine "₺123.456.789"
 * 2xl'de sığmıyordu. Masaüstü kartları 11rem'in üstünde kalır, boyutları değişmez.
 */
const VALUE_SIZE = 'text-lg @min-[9rem]:text-xl @min-[11rem]:text-2xl'

export function StatCard({
  label,
  icon,
  value,
  hint,
  change,
  valueClassName,
  busy,
  className,
  animate: animated = true,
}: {
  label: string
  /** Etiketin önündeki küçük simge (dekoratif; `aria-hidden` çağıranda). */
  icon?: ReactNode
  value: ReactNode
  hint?: string
  change?: number
  valueClassName?: string
  busy?: boolean
  className?: string
  animate?: boolean
}) {
  const reduceMotion = !!useReducedMotion()
  const changeText = change === undefined ? undefined : formatChange(change)
  const trend = change === undefined || change === 0 ? undefined : change > 0 ? 'up' : 'down'
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : undefined
  return (
    <article
      data-slot="stat-card"
      aria-busy={busy || undefined}
      className={cn(
        'border-border bg-surface rounded-surface shadow-resting @container h-full min-w-0 border px-4 py-3.5 sm:px-5 sm:py-4',
        'transition-opacity duration-(--duration-standard) ease-(--ease-standard) motion-reduce:transition-none',
        busy && 'opacity-60',
        className,
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-2 max-[380px]:flex-wrap max-[380px]:items-start">
        <span className="text-text-secondary flex min-w-0 items-center gap-1.5 text-sm [&_svg]:size-4 [&_svg]:shrink-0">
          {icon}
          {animated ? <Swap text={label} block /> : label}
        </span>
        <AnimatePresence initial={false}>
          {changeText && (
            <motion.small
              key="change"
              data-trend={trend}
              className={cn(
                'rounded-pill border-border text-text-secondary inline-flex flex-none border px-1.5 py-px text-xs tabular-nums',
                '[transition:color_var(--duration-fast)_var(--ease-standard),background-color_var(--duration-fast)_var(--ease-standard)]',
                'data-[trend=up]:text-success data-[trend=up]:border-transparent data-[trend=up]:bg-[color-mix(in_oklab,var(--success)_11%,transparent)]',
                'data-[trend=down]:text-danger data-[trend=down]:border-transparent data-[trend=down]:bg-[color-mix(in_oklab,var(--danger)_11%,transparent)]',
                'motion-reduce:transition-none',
              )}
              initial={animated ? { opacity: 0, scale: reduceMotion ? 1 : 0.96 } : false}
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: reduceMotion ? 1 : 0.96,
                transition: { duration: duration.fast, ease: ease.standard },
              }}
              transition={reduceMotion ? { duration: 0 } : spring.snappy}
            >
              {animated ? <Swap text={changeText} morph /> : changeText}
            </motion.small>
          )}
        </AnimatePresence>
      </div>
      {text !== undefined && animated ? (
        <AnimatedCounter text={text} animateOnView className={cn(VALUE_SIZE, valueClassName)} />
      ) : (
        <div
          className={cn(
            VALUE_SIZE,
            '[line-height:1] font-medium tracking-(--tracking-display) tabular-nums',
            valueClassName,
          )}
        >
          {value}
        </div>
      )}
      {hint && (
        <p className="text-text-muted leading-body mt-2 text-xs tabular-nums">
          {animated ? <Swap text={hint} block /> : hint}
        </p>
      )}
    </article>
  )
}
